import { useState, useCallback, useRef, useEffect } from 'react';
import { Search, User, Car, X, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useCustomerSearch } from '@/hooks/useCustomerSearch';
import type { CustomerSearchResult } from '@/lib/customers/types';
// Core-100 R4 wave 3 (CORE-AY D): on the search contract — one request/reply reading (useCustomerSearch), a failure is
// said as a failure, an empty answer says what was searched, protected numbers stay masked.
import { CUSTOMER_SEARCH_MIN_CHARS, CUSTOMER_SEARCH_SCOPE } from '@/lib/search/customerSearchContract';

interface CustomerSearchBarProps {
  onSelectResult: (result: CustomerSearchResult) => void;
  onClear: () => void;
  selectedResult?: CustomerSearchResult | null;
  placeholder?: string;
}

export function CustomerSearchBar({
  onSelectResult,
  onClear,
  selectedResult,
  placeholder = 'Search by name, phone, VIN, or plate...',
}: CustomerSearchBarProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const { results, isSearching, error, search, clearResults, searchedQuery } = useCustomerSearch({
    debounceMs: 300,
    limit: 10,
  });

  const handleInputChange = useCallback((value: string) => {
    setQuery(value);
    search(value);
    if (value.trim().length >= CUSTOMER_SEARCH_MIN_CHARS) {
      setIsOpen(true);
    }
  }, [search]);

  const handleSelect = useCallback((result: CustomerSearchResult) => {
    onSelectResult(result);
    setIsOpen(false);
    setQuery('');
    clearResults();
  }, [onSelectResult, clearResults]);

  const handleClear = useCallback(() => {
    onClear();
    setQuery('');
    clearResults();
    inputRef.current?.focus();
  }, [onClear, clearResults]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  if (selectedResult) {
    return (
      <div className="flex items-center gap-2 p-2 bg-muted rounded-md border">
        {selectedResult.vehicle_id ? (
          <Car className="h-4 w-4 text-muted-foreground" />
        ) : (
          <User className="h-4 w-4 text-muted-foreground" />
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">
            {selectedResult.customer_name || 'Unknown Customer'}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {selectedResult.display_label}
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0"
          onClick={handleClear}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <Popover
      open={isOpen && (results.length > 0 || isSearching || !!error
        // an answered search that found nothing is SAID (empty is not absence) — it used to close silently
        || (searchedQuery !== null && searchedQuery === query.trim()))}
      onOpenChange={setIsOpen}
    >
      <PopoverTrigger asChild>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={placeholder}
            className="pl-9 pr-9"
            data-testid="customer-search-input"
          />
          {isSearching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[var(--radix-popover-trigger-width)] p-0" 
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="max-h-80 overflow-y-auto">
          {error && (
            <div className="p-3 text-sm text-destructive" role="alert" data-testid="customer-search-failed">
              {error.message}
            </div>
          )}
          {!error && results.length === 0 && !isSearching && searchedQuery === query.trim() && (
            <div className="p-3 text-sm text-muted-foreground" data-testid="customer-search-empty">
              <p className="text-center">No customer or vehicle matched “{searchedQuery}”. Create new customer below.</p>
              <p className="mt-1 text-xs" data-testid="customer-search-scope">Searched: {CUSTOMER_SEARCH_SCOPE}</p>
            </div>
          )}
          {results.map((result, index) => (
            <button
              key={`${result.match_type}-${result.customer_id || result.vehicle_id}-${index}`}
              type="button"
              className="w-full p-3 text-left hover:bg-accent transition-colors border-b last:border-b-0 flex items-start gap-3"
              onClick={() => handleSelect(result)}
            >
              {result.vehicle_id ? (
                <Car className="h-5 w-5 text-muted-foreground mt-0.5" />
              ) : (
                <User className="h-5 w-5 text-muted-foreground mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">
                    {result.customer_name || 'Unknown'}
                  </span>
                  <Badge variant="secondary" className="text-xs shrink-0">
                    {result.match_type}
                  </Badge>
                </div>
                {result.vehicle_id && (
                  <div className="text-sm text-muted-foreground">
                    {result.vehicle_year} {result.vehicle_make} {result.vehicle_model}
                    {result.vin_masked && ` • VIN: ${result.vin_masked}`}
                    {result.plate_masked && ` • Plate: ${result.plate_masked}`}
                  </div>
                )}
                {!result.vehicle_id && result.vehicle_count > 0 && (
                  <div className="text-sm text-muted-foreground">
                    {result.vehicle_count} vehicle{result.vehicle_count > 1 ? 's' : ''} on file
                  </div>
                )}
                {(result.phone_masked || result.email_masked) && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {result.phone_masked && <span>{result.phone_masked}</span>}
                    {result.phone_masked && result.email_masked && <span> • </span>}
                    {result.email_masked && <span>{result.email_masked}</span>}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
